/**
 * 사용자 목록 컴포넌트
 * 계약별 사용자 목록을 조회하고 관리하는 컴포넌트입니다.
 * 사용자 초대, 검색, 정렬, 엑셀 다운로드 등의 기능을 제공합니다.
 */

import moment from "moment";
import qs from "qs";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { getContractDetailBasicInfo, getContractManage as getContractManageAsync } from "src/api/contract/contract-api";
import { ContractStep } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { Modal, Order, PageMeta, Select, Sort } from "src/api/public-types";
import { getUsers } from "src/api/user/user-api";
import { MemberStatus, UserListModel } from "src/api/user/user-types";
import { BaseButton, BaseInput, BaseInputWithSearch, BaseModal, BaseSelect, BaseTable } from "src/components";
import ContractSelectModal from "src/components/contract/ContractSelectModal";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import { findRepresentativeContract } from "src/pages/contract/hooks/useContractRenewal";
import { PagePath } from "src/pages/product/details";
import { qsParse, toPhoneSearchValue } from "src/utils";
import { INVITATION_CHANNEL_OPTIONS, INVITEABLE_CONTRACT_STEP } from "../constants";
import { columns, excelColumns } from "./columns/Columns";

import _ from "lodash";
import RangeDatepickerWithSelect from "src/components/RangeDatepickerWithSelect";
import SearchResetButton from "src/components/SearchResetButton";
import { ProductTypes } from "src/pages/product/product-types";
import { downloadExcelByColumns } from "src/utils/excelDownload";
import ExcelDownloadButton from "./ExcelDownloadButton";

/**
 * 정렬 기능이 비활성화된 테이블 헤더 목록
 */
const disabledSortHeaders = [
  // "contractMemberId",
  "contractManageId",
  "memberNo",
  // "email",
  "signUpTime",
  "invitationChannel",
  "inviteeMemberNo",
  "inviteeMemberId", //api 지원안함으로 제거
];

/**
 * 사용자 상태 선택 옵션
 */
const memberStatusOptions: Select[] = [
  { label: "전체", value: "" },
  { label: "초대", value: MemberStatus.MEMBER_INVITE },
  { label: "사용", value: MemberStatus.MEMBER_USE },
  { label: "삭제", value: MemberStatus.MEMBER_DELETE },
  { label: "사용종료", value: MemberStatus.MEMBER_CLOSE },
  { label: "초대만료", value: MemberStatus.MEMBER_EXPIRE },
];

/**
 * 캘린더 연동 상태 선택 옵션
 */
const calendarOptions = [
  { label: "전체", value: "" },
  { label: "연동", value: "true" },
  { label: "미연동", value: "false" },
];

/**
 * 상품 유형 선택 옵션
 */
const productTypeOptions = [
  { label: "전체", value: "" },
  { label: "공간임대", value: ProductTypes.FULL_COURT },
  { label: "공간예약", value: ProductTypes.TIME_COURT },
  { label: "관리비", value: ProductTypes.MAINTENANCE_FEE },
  { label: "비상주", value: ProductTypes.NON_RESIDENT_OFFICE },
  { label: "디지털", value: ProductTypes.DIGITAL_PRODUCT },
];

/**
 * 사용자 검색 유형 선택 옵션
 *
 * TODO(BE): 이용자 ID / 계약관리 ID / 상품 ID 3종은 CCA40(GET /ct/member) 의 searchType 확장 배포 후 동작한다.
 *           아래 코드값(CONTRACT_MEMBER_ID / CONTRACT_MANAGE_ID / PRODUCT_ID)은 예상값이므로
 *           백엔드 확정 enum 과 반드시 대조할 것. 미배포 상태에서 선택하면 조회 결과가 비거나 오류가 날 수 있다.
 */
const searchTypeUserOption: Select[] = [
  { label: "전체", value: "" },
  { label: "이용자 ID", value: "CONTRACT_MEMBER_ID" },
  { label: "회원번호", value: "MEMBER_NO" },
  { label: "휴대폰번호", value: "PHONE_NUMBER" },
  { label: "이메일", value: "EMAIL" },
  { label: "닉네임", value: "NICKNAME" },
  { label: "계약관리 ID", value: "CONTRACT_MANAGE_ID" },
  { label: "계약명", value: "CONTRACT_NAME" },
  { label: "상품 ID", value: "PRODUCT_ID" },
  { label: "상품명", value: "PRODUCT_NAME" },
];

/**
 * 캘린더 날짜 검색 유형 선택 옵션
 */
const calendarDateOption: Select[] = [
  { label: "전체", value: "" },
  { label: "초대일시", value: "INVITE_DATE" },
  { label: "만료일시", value: "INVITE_EXPIRE_DATE" },
  { label: "인증일시", value: "JOINED_DATE" },
  { label: "상태변경일시", value: "STATUS_DATE" },
];

/**
 * 인증수단 선택 옵션
 */
const verifiedTypeOption: Select[] = [
  { label: "전체", value: "" },
  { label: "휴대폰", value: "PHONE_NUMBER" },
  { label: "이메일", value: "EMAIL" },
];

/**
 * 폼 데이터 인터페이스
 * 사용자 목록 검색에 필요한 데이터 구조를 정의합니다.
 */
interface FormData {
  contractApplyNumber: string;
  /** 지정검색 입력칸 표기용. 목록 조회 필터는 contractApplyNumber 를 그대로 사용한다 */
  contractManageId?: string;
  contractId?: number;
  contractStep?: ContractStep;
  memberStatus?: string;
  search: {
    type: string;
    value: string;
  };
  watchingCalendar?: string;
  searchTimeType?: string;
  searchStartTime?: string;
  searchEndTime?: string;
  dateRange?: [Date | null, Date | null];
  verifiedType?: string;
  productTypes?: string;
  invitationChannel?: string;
}

/**
 * 사용자 목록 컴포넌트
 * 계약별 사용자 목록을 조회하고 관리하는 메인 컴포넌트입니다.
 */
const UserList = () => {
  const { isAuthority } = usePartnerAuthority();
  const navigate = useNavigate();
  const location = useLocation();
  const [modal, setModal] = useState({
    errorModal: {
      isVisible: false,
      message: "",
      type: "",
    },
    contractSelectModal: {
      visible: false,
    },
  });
  const [searchTrigger, setSearchTrigger] = useState(0); // 검색 트리거용 state 추가
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const [sort, setSort] = useState<Sort>();
  const [users, setUsers] = useState<UserListModel[]>();

  const { control, getValues, setValue, reset, watch } = useForm<FormData>();

  // API
  const { executeAsync: getUsersAsync } = useApiOperation(getUsers);
  const { executeAsync: getContractDetailBasicInfoAsync } = useApiOperation(getContractDetailBasicInfo);
  const { executeAsync: getContractManage } = useApiOperation(getContractManageAsync);

  /**
   * 사용자 목록 검색 함수
   * 검색 조건에 따라 사용자 목록을 조회합니다.
   */
  const search = ({
    data,
    sort,
    page = 0,
    size = pageMeta?.pageRequest?.size ?? 20,
  }: {
    data: FormData;
    page?: number;
    size?: number;
    sort?: { orders?: Order[] };
  }): void => {
    navigate({
      search: qs.stringify({
        size,
        page,
        sort,
        ...data,
        searchType: data.search.type,
        searchValue: toPhoneSearchValue(data.search.type, data.search.value, "PHONE_NUMBER"),

        search: undefined,
      }),
    });
    setSearchTrigger((prev) => prev + 1); // 검색
  };

  const [alertModal, setAlertModal] = useState<Modal>({
    isOpen: false,
  });

  /**
   * 페이지 이동 처리 함수
   */
  const handleGoPage = (page: number) => search({ data: getValues(), page, size: pageMeta?.pageRequest?.size });

  /**
   * 페이지 크기 변경 처리 함수
   */
  const handleViewSize = (sizeValue: number) => search({ data: getValues(), page: 0, size: sizeValue });

  /**
   * 정렬 처리 함수
   */
  const handleSort = (orders: Order[]) => search({ data: getValues(), page: pageMeta?.pageRequest.page, sort: { orders } });

  /**
   * 계약 선택 모달 닫기 함수
   */
  const closeContractSelectModal = () => setModal({ ...modal, contractSelectModal: { visible: false } });

  /**
   * 계약 선택 모달 열기 함수
   */
  const openContractSelectModal = () => setModal({ ...modal, contractSelectModal: { visible: true } });

  /**
   * 계약 선택 모달 취소 처리 함수
   */
  const onCanceldContractSelectModal = () => closeContractSelectModal();

  /**
   * 에러 모달 열기 함수
   */
  const openErrorModal = (message: string, type?: string) => {
    setModal({
      ...modal,

      errorModal: {
        type: type || "",
        isVisible: true,
        message,
      },
    });
  };

  /**
   * 에러 모달 닫기 함수
   */
  const closeErrorModal = () => {
    if (modal.errorModal.type === "contractApplyNumber") {
      // 🔄 계약 선택 모달 열기와 에러 모달 닫기를 동시에 처리
      setModal({
        ...modal,
        errorModal: {
          isVisible: false,
          message: "",
          type: "",
        },
        contractSelectModal: {
          visible: true, // 계약 선택 모달 열기
        },
      });
      return;
    }

    // 일반적인 에러 모달 닫기
    setModal({
      ...modal,
      errorModal: {
        isVisible: false,
        message: "",
        type: "",
      },
    });
  };

  /**
   * 사용자 초대 페이지로 이동하는 함수
   */
  const handleClickGoToInviteButton = async () => {
    const contractApplyNumber = getValues("contractApplyNumber");
    const contractStep = getValues("contractStep")!;

    if (!contractApplyNumber) {
      openErrorModal("계약을 선택해주세요.", "contractApplyNumber");
      return;
    }

    if (INVITEABLE_CONTRACT_STEP.indexOf(contractStep) === -1) {
      openErrorModal("초대할 수 없는 계약 상태입니다.");
      return;
    }

    let pathName = PagePath.user.form.replace(":contractManageId", String(getValues("contractId")));
    pathName += "?contractApplyNumber=" + contractApplyNumber;

    navigate(pathName);
  };

  /**
   * 계약 입력 필드 초기화 함수
   */
  const handleOnClearClickContractInput = () => {
    setValue("contractId", undefined);
    setValue("contractApplyNumber", "");
    setValue("contractManageId", "");
    setValue("contractStep", "" as any);
    search({ data: { ...getValues() } });
  };

  /**
   * 사용자 목록 조회 함수
   * 엑셀 다운로드를 위한 조회도 처리합니다.
   */
  const callList = useCallback(
    async (excelCall?: boolean) => {
      const parsedParams = qsParse(location.search);

      //쿼리스트링 기본값 객체로 세팅
      const newQueryParams = {
        page: parsedParams.page ? Number(parsedParams.page) : 0,
        size: parsedParams.size ? Number(parsedParams.size) : 20,

        memberStatus: parsedParams.memberStatus ? String(parsedParams.memberStatus) : "",
        searchType: parsedParams.searchType ? String(parsedParams.searchType) : "",
        searchValue: parsedParams.searchValue ? String(parsedParams.searchValue) : "",
        watchingCalendar: parsedParams.watchingCalendar ? String(parsedParams.watchingCalendar) : "",
        sort: parsedParams.sort ? (parsedParams.sort as unknown as Order[]) : { orders: [{ property: "inviteDate", direction: "DESC" }] },
        contractApplyNumber: parsedParams.contractApplyNumber ? String(parsedParams.contractApplyNumber) : "",
        contractManageId: parsedParams.contractManageId ? String(parsedParams.contractManageId) : "",
        contractId: parsedParams.contractId ? Number(parsedParams.contractId) : undefined,
        contractStep: parsedParams.contractStep ?? "",
        searchTimeType: parsedParams.searchTimeType ? String(parsedParams.searchTimeType) : "",
        searchStartTime: parsedParams.searchStartTime ? String(parsedParams.searchStartTime) : "",
        searchEndTime: parsedParams.searchEndTime ? String(parsedParams.searchEndTime) : "",
        verifiedType: parsedParams.verifiedType ? String(parsedParams.verifiedType) : "",
        productTypes: parsedParams.productTypes ? String(parsedParams.productTypes) : "",
        invitationChannel: parsedParams.invitationChannel ? String(parsedParams.invitationChannel) : "",
      };

      if (newQueryParams.contractApplyNumber && !newQueryParams.contractId) {
        //contractApplyNumber 가 있고 contractId가 없을때
        const manage = await getContractManage({ contractApplyNumber: newQueryParams.contractApplyNumber }).catch(() => undefined);

        if (manage) {
          // RCA18의 단건 contractId/contractStep은 하위호환 때문에 항상 원계약을 가리킨다.
          // 신청/계약번호 링크와 초대 대상이 같은 계약이어야 하므로, 대표 계약(이용중 우선, 없으면 최신 계약)으로 맞춘다.
          // 대표 계약이 초대 불가 상태면 초대 버튼에서 안내가 나간다
          const { contracts, contractId, contractManage } = manage.data.data;
          //지정검색 입력칸에 계약관리 ID를 표기하기 위해 채운다
          if (!newQueryParams.contractManageId && contractManage?.contractManageId) {
            newQueryParams.contractManageId = String(contractManage.contractManageId);
          }
          const targetContract = findRepresentativeContract(contracts);

          if (targetContract) {
            newQueryParams.contractId = Number(targetContract.contractId);
            newQueryParams.contractStep = targetContract.contractStep as ContractStep;
          } else {
            // contracts 미제공(BE 미배포 등) 시 원계약 기준으로 기존 동작 유지
            const contractResult = await getContractDetailBasicInfoAsync({ id: Number(contractId) }).catch(() => undefined);

            if (contractResult) {
              newQueryParams.contractId = Number(contractId);
              newQueryParams.contractStep = contractResult.data.data.contractStep as ContractStep;
            }
          }
        }
      }

      if (newQueryParams.contractId && !newQueryParams.contractApplyNumber) {
        //contractId가 가 있고 contractApplyNumber 없을때

        const contractResult = await getContractDetailBasicInfoAsync({ id: newQueryParams.contractId }).catch(() => undefined);

        if (contractResult) {
          newQueryParams.contractApplyNumber = contractResult.data.data.contractApplyNumber || "";
          newQueryParams.contractManageId = String(contractResult.data.data.contractManageId ?? "");
          newQueryParams.contractStep = contractResult.data.data.contractStep as ContractStep;
        }
      }

      if (newQueryParams.contractId && newQueryParams.contractApplyNumber && !newQueryParams.contractStep) {
        //[contractApplyNumber ,contractId]가 있고 contractStep이 없을 때

        const contractResult = await getContractDetailBasicInfoAsync({ id: newQueryParams.contractId }).catch(() => undefined);

        if (contractResult) {
          newQueryParams.contractStep = contractResult.data.data.contractStep as ContractStep;
        }
      }

      //계약관리 ID 가 도입되기 전에 공유된 URL 은 contractId/contractApplyNumber 만 들고 온다.
      //위 분기들이 모두 비켜가는 조합(셋 다 있는 경우)이 있어, 표기용 값은 여기서 한 번 더 보정한다
      if (newQueryParams.contractId && !newQueryParams.contractManageId) {
        const contractResult = await getContractDetailBasicInfoAsync({ id: newQueryParams.contractId }).catch(() => undefined);

        if (contractResult) {
          newQueryParams.contractManageId = String(contractResult.data.data.contractManageId ?? "");
        }
      }

      //[contractApplyNumber ,contractId] 모두 없거나 모두 있을 때 실행
      if (excelCall) {
        newQueryParams.page = 0;
        newQueryParams.size = 100;
      }

      const params: any = {
        page: newQueryParams.page,
        size: newQueryParams.size,
        sort: newQueryParams.sort,
        contractApplyNumber: newQueryParams.contractApplyNumber,
        memberStatus: newQueryParams.memberStatus,
        searchType: newQueryParams.searchType,
        searchValue: newQueryParams.searchValue,
        watchingCalendar: newQueryParams.watchingCalendar === "true" ? true : newQueryParams.watchingCalendar === "false" ? false : undefined,
        searchTimeType: newQueryParams.searchTimeType,
        searchStartTime: newQueryParams.searchStartTime,
        searchEndTime: newQueryParams.searchEndTime,
        verifiedType: newQueryParams.verifiedType,
        productTypes: newQueryParams.productTypes,
        invitationChannel: newQueryParams.invitationChannel || undefined,
      };
      let arrData: any = [];
      if (excelCall) {
        const newCall = _.cloneDeep(params);
        newCall.page = 0;
        newCall.size = 1;
        const check: any = await getUsersAsync(newCall);
        const { meta } = check.data;
        const fixedCallSize = 100;
        if (meta?.pageMeta?.totalElements > 0) {
          let callSize = Math.floor(meta?.pageMeta?.totalElements / fixedCallSize);
          const dvSize = meta?.pageMeta?.totalElements % fixedCallSize;
          if (dvSize > 0) {
            callSize = callSize + 1;
          }

          let callList: any = [];
          for (let i = 0; i < callSize; i++) {
            callList.push(getUsersAsync({ ...newCall, page: i, size: fixedCallSize }));
          }
          const result2: any = await Promise.all(callList);
          result2.forEach((response: any) => {
            arrData = [...arrData, ...response.data.data];
          });
        }
        if (arrData.length === 0) {
          setAlertModal({ isOpen: true, message: "조회된 데이터가 없습니다." });
          return;
        }

        return arrData;
      } else {
        const result = await getUsersAsync(params);
        const { data, meta } = result.data;
        setUsers(data);
        setPageMeta(meta.pageMeta);
        setSort(meta.pageMeta?.pageRequest.sort);
        reset({
          contractApplyNumber: newQueryParams.contractApplyNumber,
          contractManageId: newQueryParams.contractManageId,
          contractId: newQueryParams.contractId,
          contractStep: newQueryParams.contractStep as any,
          memberStatus: newQueryParams.memberStatus,
          search: { type: newQueryParams.searchType, value: newQueryParams.searchValue },
          watchingCalendar: newQueryParams.watchingCalendar,
          dateRange: [
            newQueryParams.searchStartTime ? moment(newQueryParams.searchStartTime).toDate() : null,
            newQueryParams.searchEndTime ? moment(newQueryParams.searchEndTime).toDate() : null,
          ],
          searchTimeType: newQueryParams.searchTimeType || "",
          searchStartTime: newQueryParams.searchStartTime,
          searchEndTime: newQueryParams.searchEndTime,
          verifiedType: newQueryParams.verifiedType,
          productTypes: newQueryParams.productTypes,
          invitationChannel: newQueryParams.invitationChannel,
        });
      }
    },
    [location.search],
  );

  useEffect(() => {
    (async () => {
      callList();
    })();
  }, [callList, searchTrigger]);

  const handleClickExcelDownloadButton = async () => {
    let result: any;

    try {
      result = await callList(true);
    } catch (error) {
      setAlertModal({ isOpen: true, message: "엑셀 다운로드에 실패하였습니다." });
      return;
    }

    const fileNamePrefix =
      watch("dateRange")?.[0] && watch("dateRange")?.[1]
        ? `이용자목록-${moment(watch("dateRange")?.[0]).format("YYYYMMDD")}~${moment(watch("dateRange")?.[1]).format("YYYYMMDD")}`
        : "이용자목록-전체";

    await downloadExcelByColumns<UserListModel>({
      data: result,
      columns: excelColumns,
      fileNamePrefix,
      onNoData: () => {
        setAlertModal({ isOpen: true, message: "조회된 데이터가 없습니다." });
      },
      onSuccess: () => {
        setAlertModal({ isOpen: true, message: "엑셀 다운로드가 완료되었습니다." });
      },
      onError: () => {
        setAlertModal({ isOpen: true, message: "엑셀 다운로드에 실패하였습니다." });
      },
    });
  };

  return (
    <div className="page-user-list">
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            <section>
              <div className="left-area__index">
                <span>지정검색</span>
              </div>
              <div className="left-area__contents">
                <div className="minmax160">
                  <Controller
                    control={control}
                    name="contractManageId"
                    render={({ field }) => (
                      <BaseInput
                        type="text"
                        value={field.value}
                        placeholder="계약관리 ID"
                        onClick={openContractSelectModal}
                        onClearClick={handleOnClearClickContractInput}
                        readonly
                      />
                    )}
                  />
                </div>
              </div>
            </section>
            <section>
              <div className="left-area__index">
                <span>조건검색</span>
              </div>
              <div className="left-area__contents">
                <div className="">
                  <RangeDatepickerWithSelect
                    stateOptions={calendarDateOption}
                    selectValue={watch("searchTimeType") || ""}
                    setStateValue={(searchTimeType: string) => {
                      // 기간 설정이 되어있을 때만 검색 실행
                      if (watch("searchStartTime") && watch("searchEndTime")) {
                        search({ data: { ...getValues(), searchTimeType } });
                      } else {
                        setValue("searchTimeType", searchTimeType);
                      }
                    }}
                    dateRange={watch("dateRange") || [null, null]}
                    onChange={(dateRange) => {
                      setValue("dateRange", dateRange);
                      const [start, end] = dateRange;

                      // 시작일과 종료일이 모두 있을 때만 검색 실행
                      if (start && end) {
                        const searchData = {
                          ...getValues(),
                          searchStartTime: moment(start).format("YYYY-MM-DD") + "T00:00:00.000+09:00",
                          searchEndTime: moment(end).format("YYYY-MM-DD") + "T23:59:59.999+09:00",
                          searchTimeType: getValues("searchTimeType") || "", // 기본값을 빈 문자열로 변경
                        };
                        search({ data: searchData });
                      }
                      if (start === null && end === null) {
                        search({ data: { ...getValues(), searchStartTime: "", searchEndTime: "", searchTimeType: "" } });
                      }
                    }}
                    onClearClick={() => {
                      search({ data: { ...getValues(), searchStartTime: "", searchEndTime: "", searchTimeType: "" } });
                    }}
                  />
                </div>

                <div className="minmax160">
                  <Controller
                    control={control}
                    name="memberStatus"
                    render={({ field }) => (
                      <BaseSelect
                        value={field.value}
                        placeholder="상태"
                        stateOptions={memberStatusOptions}
                        setStateValue={(memberStatus: string) => {
                          search({ data: { ...getValues(), memberStatus } });
                        }}
                      />
                    )}
                  />
                </div>
                <div className="minmax160">
                  <Controller
                    control={control}
                    name="verifiedType"
                    render={({ field }) => (
                      <BaseSelect
                        value={field.value}
                        placeholder="인증수단"
                        stateOptions={verifiedTypeOption}
                        setStateValue={(verifiedType: string) => {
                          search({ data: { ...getValues(), verifiedType } });
                        }}
                      />
                    )}
                  />
                </div>

                <div className="minmax160">
                  <Controller
                    control={control}
                    name="invitationChannel"
                    render={({ field }) => (
                      <BaseSelect
                        value={field.value}
                        placeholder="초대경로"
                        stateOptions={INVITATION_CHANNEL_OPTIONS}
                        setStateValue={(invitationChannel: string) => {
                          search({ data: { ...getValues(), invitationChannel } });
                        }}
                      />
                    )}
                  />
                </div>
                <div className="minmax160">
                  <Controller
                    control={control}
                    name="productTypes"
                    render={({ field }) => (
                      <BaseSelect
                        value={field.value}
                        placeholder="상품 유형"
                        stateOptions={productTypeOptions}
                        setStateValue={(productTypes: string) => {
                          search({ data: { ...getValues(), productTypes } });
                        }}
                      />
                    )}
                  />
                </div>
                <div className="minmax160">
                  <Controller
                    control={control}
                    name="watchingCalendar"
                    render={({ field }) => (
                      <BaseSelect
                        value={field.value}
                        placeholder="캘린더 연동"
                        stateOptions={calendarOptions}
                        setStateValue={(watchingCalendar: string) => {
                          search({ data: { ...getValues(), watchingCalendar } });
                        }}
                      />
                    )}
                  />
                </div>
                <div className="">
                  <Controller
                    control={control}
                    render={({ field }) => {
                      return (
                        <BaseInputWithSearch
                          type="text"
                          selectValue={field.value?.type}
                          inputValue={field.value?.value}
                          stateOptions={searchTypeUserOption}
                          setStateValue={(searchType: string) => {
                            setValue("search", {
                              ...field.value,
                              type: searchType,
                            });
                          }}
                          onChange={(searchValue: string, e: React.ChangeEvent<HTMLInputElement>) =>
                            setValue("search", {
                              ...field.value,
                              value: searchValue,
                            })
                          }
                          onKeyUp={() => {
                            search({
                              data: _.cloneDeep({ ...getValues() }),
                            });
                          }}
                          onSearchClick={() =>
                            search({
                              data: _.cloneDeep({ ...getValues() }),
                            })
                          }
                          onClearClick={() => {
                            search({
                              data: _.cloneDeep({
                                ...getValues(),
                                search: {
                                  type: "",
                                  value: "",
                                },
                              }),
                            });
                          }}
                        />
                      );
                    }}
                    name={"search"}
                  />
                </div>
                <SearchResetButton />
              </div>
            </section>
          </div>
          <div className="right-area">{isAuthority("w") && <BaseButton title="+ 이용자 초대" onClick={handleClickGoToInviteButton} />}</div>
        </div>
        {/* 샘플 */}
        {users && (
          <BaseTable
            data={users}
            columns={columns}
            pageIndex={pageMeta?.pageRequest.page || 0}
            totalPages={pageMeta?.totalPages || 0}
            currentSize={Number(pageMeta?.pageRequest?.size) || 20}
            sizeOption={handleViewSize}
            totalElements={pageMeta?.totalElements || 0}
            goPage={handleGoPage}
            disabledSortHeaders={disabledSortHeaders}
            orders={sort?.orders || []}
            setOrders={handleSort}
            children={<ExcelDownloadButton onClick={handleClickExcelDownloadButton}>엑셀받기</ExcelDownloadButton>}
          />
        )}
      </div>
      {modal.errorModal.isVisible && (
        <BaseModal isOpen={true} btnRightTitle={"확인"} onClick={closeErrorModal} onClose={closeErrorModal} title={modal.errorModal.message} />
      )}
      {/* 확인버튼만 있는 alert 모달 */}
      {alertModal.isOpen && (
        <BaseModal
          isOpen={true}
          btnRightTitle="확인"
          onClick={() => {
            setAlertModal({ isOpen: false });
          }}
          title={alertModal.message}
        />
      )}

      {modal.contractSelectModal.visible && (
        <ContractSelectModal
          onCanceled={onCanceldContractSelectModal}
          onAdded={(data) => {
            closeContractSelectModal();
            search({
              data: {
                ...getValues(),
                contractApplyNumber: data[0].contractApplyNumber,
                contractManageId: data[0].contractManageId ? String(data[0].contractManageId) : "",
                contractId: data[0].contractId,
                contractStep: data[0].contractStep,
              },
            });
          }}
          defaultCheckedContractList={[
            {
              contractId: getValues("contractId")!,
              contractApplyNumber: getValues("contractApplyNumber")!,
            },
          ]}
          contractStepFilter={INVITEABLE_CONTRACT_STEP}
        />
      )}
    </div>
  );
};

export default UserList;
